import React from 'react'
import { useEntry } from 'intersection-observer'
import { useDevice } from 'device'
import { usePathname } from 'router'
import links from 'links'
import { useAb } from 'hooks'
import { constants } from 'helpers'

import ProductsInQueueProvider from 'containers/ProductsInQueueProvider/ProductsInQueueProvider'

import ContinueStickyBar from 'compositions/landings/ContinueStickyBar/ContinueStickyBar'

import HeroSection, { type HeroSectionProps } from './components/HeroSection/HeroSection'
import BrandsRow from './components/BrandsRow/BrandsRow'
import HowItWorksSection from './components/HowItWorksSection/HowItWorksSection'
import BrandsSection from './components/BrandsSection/BrandsSection'
import PressSection from './components/PressSection/PressSection'
import QuizSection from './components/QuizSection/QuizSection'
import BestsellersSection from './components/BestsellersSection/BestsellersSection'
import BottomBanner from './components/BottomBanner/BottomBanner'
import PlanSelectSection from './components/PlanSelectSection/PlanSelectSection'
import { usePlanSelectSectionVisibility } from './util'


type HomePageViewProps = {
  isFetching: boolean
  price: string
  eventDiscountText?: SubscriptionModule.EventDiscountText
  offerType?: SubscriptionModule.OfferType
  discount?: string
  priceWithDiscount?: string
  extraShippingPrice?: string
  couponCode?: string
  heroCustomTexts?: HeroSectionProps['customTexts']
  heroCustomTextStyles?: HeroSectionProps['customTextStyles']
}

const View: React.FunctionComponent<HomePageViewProps> = (props) => {
  const {
    couponCode,
    discount,
    eventDiscountText,
    extraShippingPrice,
    heroCustomTexts,
    heroCustomTextStyles,
    isFetching,
    offerType,
    price,
    priceWithDiscount,
  } = props

  const { isMobile } = useDevice()

  const isVideoHeroEnabled = useAb(constants.abTests.homeVideoHero) !== 'a'

  // narrow rootMargin to tiny line to hide StickyBar before global footer
  const [ stickyBarAreaRef, stickyBarAreaEntry ] = useEntry({ observerProps: { rootMargin: '0% 0% -90%' } })
  const isStickyBarVisible = stickyBarAreaEntry?.isIntersecting

  const defaultSectionMargin = isMobile ? 'mt-64' : 'mt-80'

  const isPlanSectionVisible = usePlanSelectSectionVisibility(couponCode)

  const pathname = usePathname()

  const isTimerEnabled = (
    pathname === links.home
    || pathname === links.homeMen
  )

  return (
    <ProductsInQueueProvider>
      <HeroSection
        isFetching={isFetching}
        offerType={offerType}
        discount={discount}
        price={price}
        priceWithDiscount={priceWithDiscount}
        extraShippingPrice={extraShippingPrice}
        customTexts={heroCustomTexts}
        customTextStyles={heroCustomTextStyles}
        isTimerEnabled={isTimerEnabled}
        isVideoHeroEnabled={isVideoHeroEnabled}
      />
      <BrandsRow />
      <HowItWorksSection className={defaultSectionMargin} />
      <BrandsSection />
      <QuizSection className={isMobile ? null : defaultSectionMargin} />
      <PressSection className={defaultSectionMargin} />
      <div ref={stickyBarAreaRef}>
        <BestsellersSection className={defaultSectionMargin} />
        <ContinueStickyBar
          isVisible={isStickyBarVisible}
          eventDiscountText={eventDiscountText}
          coupon={couponCode}
        />
      </div>
      {
        isPlanSectionVisible && (
          <PlanSelectSection
            className={defaultSectionMargin}
            couponCode={couponCode}
          />
        )
      }
      <BottomBanner className={isPlanSectionVisible ? null : defaultSectionMargin} />
    </ProductsInQueueProvider>
  )
}


export default View
